import { ChevronUpIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  ButtonGroup,
  Divider,
  Heading,
  HStack,
  Spinner,
} from '@chakra-ui/react';
import IconBookmarkCheck from 'central-icons/IconBookmarkCheck';
import { Controller } from 'react-hook-form';
import type { SingleValue } from 'react-select';

import {
  Button,
  FormField,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Select,
  Text,
} from '@revelio/ui';

import { DataBuilderFooter } from '../components/DataBuilderFooter';
import type { ConfigSummaryDetail } from '../components/ui/ConfigSummaryList';
import { ConfigSummaryList } from '../components/ui/ConfigSummaryList';
import { DataBuilderStepLayout } from '../components/ui/DataBuilderStepLayout';
import { DatasetSummaryCard } from '../components/ui/DatasetSummaryCard';
import { dataLagDisplayValue } from '../utils/data-lag-helper';
import { Step4Layout } from './step4-confirmation/Step4Layout';
import styles from './step4-confirmation/step4-layout.module.css';
import { useConfirmationSummary } from './step4-confirmation/useConfirmationSummary';
import { useSaveDataset } from './step4-confirmation/useSaveDataset';
import {
  type ClientOption,
  useVerifySubmit,
} from './step4-confirmation/useVerifySubmit';

const DELIVERY_METHOD_LABELS = {
  download: 'Download',
  snowflake: 'Snowflake',
  s3: 'AWS S3',
} as const;

export const DataBuilderConfirmationStep = () => {
  const {
    datasetSummaries,
    selectionLists,
    deliveryMethod,
    deliveryFormat,
    snowflakeConfig,
    s3Config,
    lagData,
  } = useConfirmationSummary();

  const deliveryDetails: ConfigSummaryDetail[] = (() => {
    const methodLabel =
      DELIVERY_METHOD_LABELS[
        deliveryMethod as keyof typeof DELIVERY_METHOD_LABELS
      ];
    const dataLagDetails =
      lagData > 0
        ? [
            {
              label: 'Data lag',
              value: dataLagDisplayValue(lagData),
            },
          ]
        : [];

    if (deliveryMethod === 'snowflake' && snowflakeConfig !== null) {
      return [
        { label: 'Method', value: methodLabel },
        ...(snowflakeConfig.database !== undefined
          ? [{ label: 'Database', value: snowflakeConfig.database }]
          : []),
        ...(snowflakeConfig.schema !== undefined
          ? [{ label: 'Schema', value: snowflakeConfig.schema }]
          : []),
        { label: 'Table', value: snowflakeConfig.table },
        ...dataLagDetails,
      ];
    }

    if (deliveryMethod === 's3' && s3Config !== null) {
      return [
        { label: 'Method', value: methodLabel },
        ...(s3Config.bucket !== undefined
          ? [{ label: 'Bucket', value: s3Config.bucket }]
          : []),
        { label: 'Path', value: s3Config.path },
        { label: 'Format', value: s3Config.format },
        ...dataLagDetails,
      ];
    }

    return [
      { label: 'Delivery', value: `${methodLabel} (${deliveryFormat})` },
      ...dataLagDetails,
    ];
  })();
  const {
    control,
    register,
    errors,
    clientOptions,
    isRevelioAdmin,
    verifyStatus,
    handleConfirmationSubmit,
    submitButtonText,
    errorMessage,
    canSubmit,
    companySelectionGateStatus,
  } = useVerifySubmit();
  const {
    handleSaveAndSubmit,
    handleUpdateAndSubmit,
    handleCreateNewAndSubmit,
    saveStatus,
    canSaveAndSubmit,
    isEditingLoadedSavedSet,
    canManageDatasets,
  } = useSaveDataset();
  const clientErrorMessages =
    errors.client?.message !== undefined ? [errors.client.message] : undefined;
  const nameErrorMessages =
    errors.name?.message !== undefined ? [errors.name.message] : undefined;
  const saveAndSubmitMenuItems = (() => {
    if (isEditingLoadedSavedSet) {
      return (
        <>
          <MenuItem
            icon={<IconBookmarkCheck size={16} />}
            disabled={!canSaveAndSubmit}
            onClick={handleUpdateAndSubmit}
            data-testid="data-builder-update-and-submit"
          >
            Update & Submit
          </MenuItem>
          <MenuItem
            icon={<IconBookmarkCheck size={16} />}
            disabled={!canSaveAndSubmit}
            onClick={handleCreateNewAndSubmit}
            data-testid="data-builder-create-new-and-submit"
          >
            Create New & Submit
          </MenuItem>
        </>
      );
    }

    return (
      <MenuItem
        icon={<IconBookmarkCheck size={16} />}
        disabled={!canSaveAndSubmit}
        onClick={handleSaveAndSubmit}
        data-testid="data-builder-save-and-submit"
      >
        Save & Submit
      </MenuItem>
    );
  })();

  return (
    <DataBuilderStepLayout
      title="Confirmation"
      noGutter
      footer={
        <DataBuilderFooter
          actions={
            <ButtonGroup size="sm" isAttached variant="outline">
              <Button
                data-testid="data-builder-confirmation-submit"
                color="primary"
                size="sm"
                disabled={!canSubmit}
                onClick={handleConfirmationSubmit}
              >
                <HStack gap={2}>
                  {verifyStatus.status === 'pending' && <Spinner size="xs" />}
                  <span>{submitButtonText}</span>
                </HStack>
              </Button>
              {canManageDatasets && (
                <Menu>
                  <MenuButton>
                    <IconButton
                      data-testid="data-builder-confirmation-actions-menu-button"
                      aria-label="More confirmation actions"
                      icon={<ChevronUpIcon />}
                      variant="solid"
                      shape="squared"
                      className={styles.splitMenuButton}
                    />
                  </MenuButton>
                  <MenuList
                    side="top"
                    align="end"
                    className={styles.splitMenuList}
                  >
                    {saveAndSubmitMenuItems}
                  </MenuList>
                </Menu>
              )}
            </ButtonGroup>
          }
        />
      }
    >
      <Step4Layout
        bottomContent={
          <div>
            <Box maxWidth="100%" overflowX="auto" wordBreak="break-word">
              {companySelectionGateStatus === 'error' && (
                <Alert
                  status="error"
                  data-testid="data-builder-company-gate-error"
                >
                  <AlertIcon />
                  <AlertTitle>Company reference failed</AlertTitle>
                  <AlertDescription>
                    Creating the company reference table failed. Go back to
                    Columns &amp; Filters to update your company selection and
                    try again.
                  </AlertDescription>
                </Alert>
              )}

              {verifyStatus.status === 'error' && (
                <Alert status="error" data-testid="data-builder-verify-error">
                  <AlertIcon />
                  <AlertTitle>Submission failed</AlertTitle>
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
              )}

              {saveStatus.status === 'error' && (
                <Alert status="error" data-testid="data-builder-save-error">
                  <AlertIcon />
                  <AlertTitle>Save failed</AlertTitle>
                  <AlertDescription>
                    {saveStatus.error instanceof Error
                      ? saveStatus.error.message
                      : 'An error occurred.'}
                  </AlertDescription>
                </Alert>
              )}
            </Box>

            {isRevelioAdmin && (
              <FormField
                label="Client"
                errors={clientErrorMessages}
                style={{ marginBottom: '16px' }}
              >
                <Controller
                  control={control}
                  name="client"
                  rules={undefined} // rules={{ required: 'Client is required' }} // This is intentionally optional. Ask Kevin or Max if you have questions.
                  render={({ field }) => (
                    <Select
                      options={clientOptions}
                      value={clientOptions.find((o) => o.value === field.value)}
                      onChange={(selected: SingleValue<ClientOption>) => {
                        field.onChange(selected?.value ?? '');
                      }}
                      placeholder="Select client..."
                      data-testid="data-builder-client-select"
                    />
                  )}
                />
              </FormField>
            )}

            <FormField
              label="Name"
              supportingText="Alphanumeric characters, underscores, and spaces"
              errors={nameErrorMessages}
            >
              <Input
                {...register('name', {
                  required: 'Name is required.',
                  minLength: {
                    value: 1,
                    message: 'Minimum length should be 1',
                  },
                  pattern: {
                    value: /^[a-zA-Z0-9_\s]*$/,
                    message:
                      'Only alphanumeric characters, underscores, and spaces are allowed',
                  },
                  setValueAs: (value) =>
                    typeof value === 'string' ? value.trim() : value,
                  validate: (value) =>
                    (typeof value === 'string' && value.trim().length > 0) ||
                    'Name cannot be empty or whitespace only',
                })}
                data-testid="data-builder-name-input"
              />
            </FormField>
          </div>
        }
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            marginBottom: '1rem',
          }}
          data-testid="data-builder-confirmation-step"
        >
          <Heading fontWeight="600" fontSize="16px" color="text.primary">
            Datasets
          </Heading>

          {datasetSummaries.map((dataset) => (
            <DatasetSummaryCard
              key={dataset.instanceId}
              dataset={dataset}
              selectionLists={selectionLists}
            />
          ))}

          {datasetSummaries.length === 0 && (
            <Text fontSize="sm" style={{ paddingLeft: '20px' }}>
              No datasets selected
            </Text>
          )}

          <Divider className={styles.divider} />

          <Heading fontWeight="600" fontSize="16px" color="text.primary">
            Delivery
          </Heading>
          <ConfigSummaryList details={deliveryDetails} />
        </div>
      </Step4Layout>
    </DataBuilderStepLayout>
  );
};
